import { useState, useEffect } from "react";

const PWAInstallModal = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    // Enhanced mobile and browser detection
    const checkDevice = () => {
      const userAgent = navigator.userAgent;
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        );
      const isSmallScreen = window.innerWidth <= 768;
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;

      const mobile = isMobileDevice || isSmallScreen || isTouchDevice;
      const iOS = /iPad|iPhone|iPod/.test(userAgent);

      // Detect Safari on iOS
      const safari =
        iOS &&
        /Safari/.test(userAgent) &&
        !/CriOS|FxiOS|OPiOS|mercury/i.test(userAgent);

      // Enhanced standalone detection for iOS
      const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true ||
        localStorage.getItem("pwa-installed") === "true" ||
        // Check if launched from home screen on iOS
        (iOS && window.navigator.standalone) ||
        // Check if running in standalone mode
        window.matchMedia("(display-mode: fullscreen)").matches;

      return { mobile, iOS, safari, standalone };
    };

    const device = checkDevice();
    setIsMobile(device.mobile);
    setIsIOS(device.iOS);
    setIsSafari(device.safari);
    setIsStandalone(device.standalone);

    console.log("📱 Device detection:", device);

    // Don't proceed if not mobile or already installed
    if (!device.mobile || device.standalone) {
      console.log("❌ Not showing modal - not mobile or already installed");
      return;
    }

    // Check if dismissed recently (shorter time for testing)
    const lastDismissed = localStorage.getItem("pwa-install-dismissed");
    if (lastDismissed) {
      const hoursSinceDismissal =
        (Date.now() - parseInt(lastDismissed, 10)) / (1000 * 60 * 60);
      if (hoursSinceDismissal < 1) {
        console.log("⏰ Recently dismissed, not showing modal");
        return;
      } else {
        localStorage.removeItem("pwa-install-dismissed");
      }
    }

    // Listen for beforeinstallprompt event (Android/Chrome only)
    const handleBeforeInstallPrompt = (e) => {
      console.log("🚀 beforeinstallprompt event fired!");
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    // Handle successful installation
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setCanInstall(false);
      setShowInstallModal(false);
      setIsStandalone(true);
      localStorage.setItem("pwa-installed", "true");

      // Show success notification when app is actually installed
      showSuccessNotification("PWA downloaded successfully!");
    };

    // Handle window resize
    const handleResize = () => {
      const newDevice = checkDevice();
      setIsMobile(newDevice.mobile);
      setIsStandalone(newDevice.standalone);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("resize", handleResize);

    // Show modal for all mobile devices after a delay
    const timer = setTimeout(() => {
      if (device.iOS) {
        console.log("🍎 Showing iOS install modal");
      } else {
        console.log("🤖 Showing Android install modal");
      }
      setShowInstallModal(true);
    }, 2000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Android: Show success notification only when actually installed
  const showSuccessNotification = (message) => {
    const notification = document.createElement("div");
    notification.innerHTML = `
      <div style="
        display: flex; align-items: center; justify-content: center;
        padding: 16px 24px; font-size: 16px; font-weight: 600;
      ">
        <span style="margin-right: 12px; font-size: 24px;">✅</span>
        <span>${message}</span>
      </div>
    `;

    notification.style.cssText = `
      position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
      background: #4CAF50; color: white; border-radius: 12px; z-index: 10001;
      min-width: 280px; max-width: 90%; box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      animation: slideDown 0.3s ease-out;
    `;

    // Add animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes slideDown {
        from { transform: translateX(-50%) translateY(-100%); opacity: 0; }
        to { transform: translateX(-50%) translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(notification);

    // Auto remove after 3 seconds
    setTimeout(() => {
      if (document.body.contains(notification)) {
        notification.style.animation = "slideDown 0.3s ease-out reverse";
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
          if (document.head.contains(style)) {
            document.head.removeChild(style);
          }
        }, 300);
      }
    }, 3000);
  };

  const handleInstallClick = async () => {
    console.log("🔘 Install button clicked", {
      isIOS,
      isSafari,
      canInstall,
      hasDeferredPrompt: !!deferredPrompt,
    });

    // ANDROID: Proper PWA installation with validation
    if (!isIOS) {
      try {
        console.log("🤖 Starting Android PWA installation...");

        // Check if deferred prompt is available (required for proper installation)
        if (!deferredPrompt || !canInstall) {
          console.log("❌ No deferred prompt available - PWA not installable");

          // Show helpful message instead of false success
          alert(
            "📱 Installation not available\n\nPlease use your browser's menu:\n1. Tap the three dots (⋮)\n2. Select 'Add to Home Screen'\n3. Tap 'Add'"
          );
          setShowInstallModal(false);
          return;
        }

        // Use deferred prompt for proper installation
        console.log("📲 Using deferred prompt for installation...");

        // Show the browser's native install dialog
        const promptResult = await deferredPrompt.prompt();
        console.log("� Prompt triggered, result:", promptResult);

        // Wait for user choice
        const choiceResult = await deferredPrompt.userChoice;
        console.log("� User choice result:", choiceResult);

        if (choiceResult.outcome === "accepted") {
          console.log("✅ User accepted installation");
          setDeferredPrompt(null);
          setCanInstall(false);
          setIsStandalone(true);
          localStorage.setItem("pwa-installed", "true");
          showSuccessNotification("PWA downloaded successfully!");
        } else {
          console.log("❌ User dismissed installation");
          // Don't show success notification if user cancelled
        }

        setShowInstallModal(false);
        return;
      } catch (error) {
        console.error("💥 Android installation error:", error);

        // Show error message instead of false success
        alert(
          "⚠️ Installation failed\n\nPlease try:\n1. Using browser menu 'Add to Home Screen'\n2. Checking your internet connection\n3. Trying again later"
        );
        setShowInstallModal(false);
        return;
      }
    }

    // IPHONE: Keep current instruction behavior (unchanged)
    if (isIOS) {
      try {
        if (isSafari && "BeforeInstallPromptEvent" in window) {
          console.log("🍎 Attempting Safari direct install");
        }

        if (isSafari) {
          console.log("🍎 Safari detected - showing Safari instructions");
          setTimeout(() => {
            window.location.hash = "#add-to-homescreen";
          }, 100);
        }

        // Show instructions for manual installation (KEEP CURRENT ALERT FOR iOS)
        alert(`📱 Install FP One on iPhone/iPad:

${
  isSafari
    ? "✅ Perfect! You're using Safari"
    : "⚠️ For best experience, open this page in Safari"
}

Quick Steps:
1️⃣ Tap the Share button (⎘) ${isSafari ? "at the bottom" : "in Safari"}
2️⃣ Scroll down and tap "Add to Home Screen"
3️⃣ Tap "Add" to install

✨ The app will appear on your home screen and work like a native app!

${
  !isSafari
    ? "\n💡 Tip: Copy this link and open it in Safari for the best installation experience."
    : ""
}`);
      } catch (error) {
        console.log(
          "🍎 iOS direct install not available, showing instructions"
        );
      }

      setShowInstallModal(false);
      localStorage.setItem("pwa-installed", "true");
      return;
    }
  };

  const handleCloseModal = () => {
    setShowInstallModal(false);
    localStorage.setItem("pwa-install-dismissed", Date.now().toString());
  };

  // Don't show modal if conditions not met
  if (isStandalone || !showInstallModal || !isMobile) {
    return null;
  }

  return (
    <div className="pwa-install-overlay">
      <div className="pwa-install-modal">
        <div className="pwa-close-btn" onClick={handleCloseModal}>
          ×
        </div>

        <div className="pwa-modal-content">
          <div className="pwa-icon">📱</div>
          <h3>Install FP One</h3>
          <p>Get instant access to our app directly from your home screen!</p>

          {isIOS ? (
            <div className="ios-instructions">
              <h4>📱 iPhone/iPad Installation:</h4>
              {isSafari ? (
                <div className="safari-ready">
                  <p>
                    ✅ <strong>Ready to install!</strong>
                  </p>
                  <p>Tap the button below and follow the simple steps.</p>
                </div>
              ) : (
                <div className="chrome-ios">
                  <p>
                    ⚠️ <strong>For best results, use Safari browser</strong>
                  </p>
                  <p>Or tap the button below for instructions.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="android-instructions">
              <h4>🤖 Android Installation:</h4>
              <p>
                Tap the button below to download the app directly to your home
                screen.
              </p>
              <div
                style={{
                  background: "#e8f5e8",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #4caf50",
                  marginTop: "12px",
                }}
              >
                <p
                  style={{
                    margin: "0",
                    color: "#2e7d32",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  ✅ Ready for direct download!
                </p>
              </div>
            </div>
          )}

          <div className="pwa-buttons">
            <button className="pwa-install-btn" onClick={handleInstallClick}>
              Install App
            </button>
            <button className="pwa-later-btn" onClick={handleCloseModal}>
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallModal;
